import {
  SchemaBlock,
  PDPSchemaData,
  CollectionPageSchemaData,
  OfferCatalogSchemaData,
  SchemaConfig,
} from "../types/schema.types";
import { buildProductSchema } from "../builders/product.builder";
import { buildBreadcrumbSchema } from "../builders/breadcrumb.builder";
import { buildCollectionPageSchema } from "../builders/collection-page.builder";
import { buildOfferCatalogSchema } from "../builders/offer-catalog.builder";
import {
  buildOrganizationSchema,
  OrganizationData,
} from "../builders/organization.builder";

/**
 * Centralized schema registry for generating page-specific structured data
 */
export class SchemaRegistry {
  /**
   * Generates Schema.org structured data for Product Detail Pages
   * @param data - Product data and breadcrumbs for PDP
   * @param options - Configuration options for schema generation
   * @returns Array of SchemaBlock objects for the PDP
   */
  static pdp(data: PDPSchemaData, options: SchemaConfig = {}): SchemaBlock[] {
    const schemas: SchemaBlock[] = [];

    // Always include product schema
    const productSchema = buildProductSchema(data.product);
    schemas.push(productSchema);

    // Include breadcrumb schema if breadcrumbs are provided and not disabled
    const includeBreadcrumbs = options.includeBreadcrumbs !== false;
    if (includeBreadcrumbs && data.breadcrumbs && data.breadcrumbs.length > 0) {
      const breadcrumbSchema = buildBreadcrumbSchema(data.breadcrumbs);
      schemas.push(breadcrumbSchema);
    }

    return schemas;
  }

  /**
   * Generates Schema.org structured data for Collection Pages (Brand/Category pages)
   * @param data - Collection page data and breadcrumbs
   * @param options - Configuration options for schema generation
   * @returns Array of SchemaBlock objects for the Collection Page
   */
  static collectionPage(
    data: CollectionPageSchemaData,
    options: SchemaConfig = {}
  ): SchemaBlock[] {
    const schemas: SchemaBlock[] = [];

    // Generate collection page schema with embedded breadcrumbs
    const collectionPageSchema = buildCollectionPageSchema(data.collectionPage);
    schemas.push(collectionPageSchema);

    return schemas;
  }

  /**
   * Generates Schema.org structured data for Offer Catalog Pages (Product Listing Pages)
   * @param data - Offer catalog data including products and breadcrumbs
   * @param options - Configuration options for schema generation
   * @returns Array of SchemaBlock objects for the Offer Catalog Page
   */
  static offerCatalog(
    data: OfferCatalogSchemaData,
    options: SchemaConfig = {}
  ): SchemaBlock[] {
    const schemas: SchemaBlock[] = [];

    // Generate offer catalog schema with embedded products and breadcrumbs
    const offerCatalogSchema = buildOfferCatalogSchema(data.offerCatalog);
    schemas.push(offerCatalogSchema);

    return schemas;
  }

  /**
   * Generates Schema.org structured data for Organization (Homepage)
   * @param data - Organization data
   * @returns Array of SchemaBlock objects for the Organization
   */
  static organization(data: OrganizationData): SchemaBlock[] {
    const schemas: SchemaBlock[] = [];

    // Generate organization schema
    const organizationSchema = buildOrganizationSchema(data);
    schemas.push(organizationSchema);

    return schemas;
  }
}
